import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Save } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { companyRoleLabels } from "@/utils/companyLabels";
import { requiresWaterLocate } from "@/utils/waterLocateRules";
import {
  CustomerSupervisorSelect,
  ProjectManagerDropdown,
} from "@/components/ProjectAssignmentControls";
import { TypedDateInput } from "@/components/TypedDateInput";
import { PermitCalculatedBadge, todayISO } from "@/components/PermitCalculatedBadge";
import { formatInformationalStatus } from "@/components/InformationalStatusText";

const LOCATE_CONDITIONS = ["valid", "trouble", "remark"];
import { LIFECYCLE_STAGES as stages, lifecycleStageChoices } from "@/utils/lifecycleStages";
import { createConflictHandler, attachExpectedVersion } from "@/lib/conflictHandler";
import { formatTimestamp } from "@/utils/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const projectFields = [
  "project_code",
  "name",
  "client",
  "city",
  "customer_supervisor",
  "scheduled_date",
  "due_date",
  "permit_required",
  "locate_required",
  "water_locate_required",
  "water_locate_submitted_at",
  "work_scope",
  "remaining_work",
  "project_manager_user_ids",
];

const columnPanels = {
  project_number: "project",
  official_status: "stage",
  customer: "project",
  customer_supervisor: "project",
  project_manager_names: "project",
  permit_status: "permits",
  permit_expiration: "permits",
  locate_status: "locates",
  locate_ticket_number: "locates",
  water_locate: "project",
  created_at: "platform",
  next_scheduled_date: "project",
  due_date: "project",
  remaining_work: "project",
  next_required_action: "project",
};

const createDraft = (project, canAccessBilling) => {
  const draft = projectFields.reduce((values, field) => {
    values[field] = project?.[field] ?? (
      field === "project_manager_user_ids"
        ? []
        : ["permit_required", "locate_required", "water_locate_required"].includes(field)
          ? false
          : ""
    );
    return values;
  }, {});

  if (canAccessBilling) {
    draft.what_is_invoiced = project?.what_is_invoiced ?? "";
  }

  return draft;
};

const emptySupportRecord = (projectId, permitType) => ({
  project_id: projectId,
  permit_type: permitType,
  reference_number: "",
  request_date: "",
  expires_on: "",
  status: "missing",
  notes: "",
  ticket_not_needed: false,
  ticket_description: "",
  active: true,
});

const supportTypes = {
  permits: "Permit",
  locates: "Locate / 811",
};

export default function MasterBoardQuickEditor({
  canAccessBilling,
  canManageRecords,
  companyContext,
  initialColumn,
  initialPanel,
  onClose,
  onSaved,
  projectId,
}) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [users, setUsers] = useState([]);
  const [activePanel, setActivePanel] = useState(initialPanel || columnPanels[initialColumn] || "project");
  const [savingProject, setSavingProject] = useState(false);
  const [savingStage, setSavingStage] = useState(false);
  const [savingRecordId, setSavingRecordId] = useState("");
  const [creatingType, setCreatingType] = useState("");
  const [stage, setStage] = useState("not_started");
  const [blockerActive, setBlockerActive] = useState(false);
  const [blockerReason, setBlockerReason] = useState("");
  const [completionNotes, setCompletionNotes] = useState("");
  const [generalNotes, setGeneralNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const load = useCallback(async () => {
    const response = await axios.get(`${API}/projects/${projectId}`);
    setData(response.data);
    setDraft(createDraft(response.data.project, canAccessBilling));
    setStage(response.data.project.lifecycle_state || "not_started");
    setBlockerActive(Boolean(response.data.project.operational_blocker));
    setBlockerReason(response.data.project.operational_blocker_reason || "");
  }, [canAccessBilling, projectId]);

  useEffect(() => {
    load().catch((error) => {
      toast.error(error.response?.data?.detail || "Project details could not be loaded.");
      onCloseRef.current();
    });
  }, [load]);

  useEffect(() => {
    setActivePanel(initialPanel || columnPanels[initialColumn] || "project");
  }, [initialColumn, initialPanel, projectId]);

  useEffect(() => {
    if (!data) {
      return;
    }
    axios
      .get(`${API}/assignable-users`)
      .then((response) => setUsers(response.data.users || []))
      .catch(() => setUsers([]));
  }, [data]);

  const loadGeneralNotes = useCallback(async () => {
    const response = await axios.get(`${API}/projects/${projectId}/general-notes`);
    setGeneralNotes(response.data.items || []);
  }, [projectId]);

  useEffect(() => {
    loadGeneralNotes().catch(() => setGeneralNotes([]));
  }, [loadGeneralNotes]);

  const addGeneralNote = async () => {
    const text = noteDraft.trim();
    if (!text) {
      return;
    }
    setAddingNote(true);
    try {
      await axios.post(`${API}/projects/${projectId}/general-notes`, {
        notes: text,
        additional_notes: "",
      });
      setNoteDraft("");
      toast.success("Project note added to Communication History");
      await loadGeneralNotes();
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add project note.");
    } finally {
      setAddingNote(false);
    }
  };

  const project = data?.project;
  const companyLabels = companyRoleLabels(companyContext || data?.company_context);
  const permits = useMemo(
    () => (data?.permits || []).filter((record) => record.active !== false && record.permit_type === "Permit"),
    [data],
  );
  const locates = useMemo(
    () => (data?.permits || []).filter((record) => record.active !== false && record.permit_type !== "Permit"),
    [data],
  );
  const stageChoices = lifecycleStageChoices(project, { canAccessBilling });

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const saveProject = async (event) => {
    event.preventDefault();
    const changes = Object.entries(draft).reduce((result, [field, value]) => {
      const priorValue = project?.[field] ?? (field === "project_manager_user_ids" ? [] : "");
      if (JSON.stringify(value) !== JSON.stringify(priorValue)) {
        result[field] = value;
      }
      return result;
    }, {});

    if (!Object.keys(changes).length) {
      toast.message("No project changes to save.");
      return;
    }

    setSavingProject(true);
    const handleConflict = createConflictHandler(async () => {
      await load();
      await onSaved();
    });
    try {
      await axios.patch(
        `${API}/projects/${projectId}`,
        attachExpectedVersion(changes, project),
      );
      toast.success("Project information saved.");
      await load();
      await onSaved();
    } catch (error) {
      if (!handleConflict(error)) {
        toast.error(error.response?.data?.detail || "Project information could not be saved.");
      }
    } finally {
      setSavingProject(false);
    }
  };

  const saveStage = async () => {
    if (["complete", "closed"].includes(stage)) {
      setSavingStage(true);
      try {
        await axios.post(`${API}/projects/${projectId}/lifecycle`, {
          lifecycle_state: stage,
          notes: completionNotes,
        });
        toast.success(stage === "closed" ? "Project closed." : "Project completion recorded.");
        await load();
        await onSaved();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Project state could not be saved.");
      } finally {
        setSavingStage(false);
      }
      return;
    }
    setSavingStage(true);
    try {
      if (stage !== project.lifecycle_state) {
        if (stage === "blocked") {
          await axios.post(`${API}/projects/${projectId}/blocker/create`, {
            reason: blockerReason.trim(),
          });
        } else {
          await axios.post(`${API}/projects/${projectId}/lifecycle${canAccessBilling ? "/override" : ""}`, {
            lifecycle_state: stage,
            reason: "",
          });
        }
      }
      if (blockerActive && !project.operational_blocker) {
        await axios.post(`${API}/projects/${projectId}/blocker/create`, {
          reason: blockerReason.trim(),
        });
      }
      if (blockerActive && project.operational_blocker && blockerReason !== (project.operational_blocker_reason || "")) {
        await axios.patch(`${API}/projects/${projectId}/blocker`, { reason: blockerReason.trim() });
      }
      if (!blockerActive && project.operational_blocker) {
        if (stage === "blocked") {
          toast.error("Choose the next official Project Stage before releasing the Roadblock.");
          return;
        }
        await axios.post(`${API}/projects/${projectId}/blocker/resolve`, {
          lifecycle_state: stage,
        });
      }
      toast.success("Official project status saved.");
      await load();
      await onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Project status could not be saved.");
    } finally {
      setSavingStage(false);
    }
  };


  const saveSupportRecord = async (record) => {
    setSavingRecordId(record.id);
    try {
      await axios.patch(`${API}/permits/${record.id}`, supportPayload(record));
      toast.success(`${record.permit_type} saved.`);
      await load();
      await onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Permit or locate could not be saved.");
    } finally {
      setSavingRecordId("");
    }
  };

  const createSupportRecord = async (type) => {
    setCreatingType(type);
    try {
      const record = emptySupportRecord(projectId, supportTypes[type]);
      if (type === "locates") record.ticket_not_needed = true;
      await axios.post(`${API}/permits`, record);
      toast.success(type === "locates" ? "Locate draft added — set an expiration date to activate it." : "New permit added.");
      await load();
      await onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Permit or locate could not be added.");
    } finally {
      setCreatingType("");
    }
  };

  const updateSupportField = (recordId, field, value) => {
    setData((current) => ({
      ...current,
      permits: current.permits.map((record) => (
        record.id === recordId ? { ...record, [field]: value } : record
      )),
    }));
  };

  const deleteSupportRecord = async (record) => {
    if (!window.confirm(`Permanently delete this ${record.permit_type}? This cannot be undone.`)) return;
    setSavingRecordId(record.id);
    try {
      await axios.delete(`${API}/permits/${record.id}`);
      toast.success(`${record.permit_type} permanently deleted.`);
      await load();
      await onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Permit or locate could not be deleted.");
    } finally {
      setSavingRecordId("");
    }
  };

  if (!data || !project) {
    return null;
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="master-quick-editor-dialog" data-testid="master-board-quick-editor">
        <DialogHeader>
          <DialogTitle>{project.project_code} quick editor</DialogTitle>
          <DialogDescription>
            Update the selected board information without leaving the Master Board.
          </DialogDescription>
        </DialogHeader>

        <div className="master-quick-editor-tabs" data-testid="master-board-editor-tabs">
          {[
            ["project", "Project information"],
            ["stage", "Project Stage"],
            ["permits", "Permits"],
            ["locates", "Locates / 811"],
            ["platform", "Platform details"],
          ].map(([panel, label]) => (
            <button
              className={activePanel === panel ? "is-active" : ""}
              data-testid={`master-editor-tab-${panel}`}
              key={panel}
              onClick={() => setActivePanel(panel)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>

        <div className="master-quick-editor-body">
          {activePanel === "project" && (
            <form data-testid="master-editor-project-form" onSubmit={saveProject}>
              <div className="master-quick-editor-grid">
                <Field label="Project number" testId="master-editor-project-code">
                  <input onChange={(event) => updateDraft("project_code", event.target.value)} required value={draft.project_code} />
                </Field>
                <Field label="Project name" testId="master-editor-project-name">
                  <input onChange={(event) => updateDraft("name", event.target.value)} required value={draft.name} />
                </Field>
                <Field label="Customer" testId="master-editor-customer">
                  <input onChange={(event) => updateDraft("client", event.target.value)} required value={draft.client} />
                </Field>
                {canManageRecords && <Field label="City" testId="master-editor-city">
                  <input onChange={(event) => {
                    const city = event.target.value;
                    updateDraft("city", city);
                    if (requiresWaterLocate(city)) {
                      updateDraft("water_locate_required", true);
                    }
                  }} value={draft.city} />
                </Field>}
                <Field label={`Assigned ${companyLabels.projectManager}s`} testId="master-editor-project-managers" wide>
                  <ProjectManagerDropdown
                    onChange={(value) => updateDraft("project_manager_user_ids", value)}
                    testId="master-editor-project-managers-input"
                    users={users}
                    value={draft.project_manager_user_ids || []}
                  />
                </Field>
                <Field label="Customer supervisor" testId="master-editor-customer-supervisor">
                  <CustomerSupervisorSelect
                    onChange={(value) => updateDraft("customer_supervisor", value)}
                    testId="master-editor-customer-supervisor-input"
                    value={draft.customer_supervisor}
                  />
                </Field>
                <Field label="Scheduled Date" testId="master-editor-scheduled-date">
                  <TypedDateInput data-testid="master-quick-scheduled-date" onChange={(value) => updateDraft("scheduled_date", value)} value={draft.scheduled_date} />
                </Field>
                <Field label="Due Date" testId="master-editor-due-date">
                  <TypedDateInput data-testid="master-quick-due-date" onChange={(value) => updateDraft("due_date", value)} value={draft.due_date} />
                </Field>
                {canManageRecords && <Field label="Permit Requirement" testId="master-editor-permit-requirement">
                  <select
                    data-testid="master-editor-permit-requirement-input"
                    onChange={(event) => updateDraft("permit_required", event.target.value === "required")}
                    value={draft.permit_required ? "required" : "not_required"}
                  >
                    <option value="not_required">Not Required</option>
                    <option value="required">Required</option>
                  </select>
                </Field>}
                {canManageRecords && <Field label="Locate / 811 Requirement" testId="master-editor-locate-requirement">
                  <select
                    data-testid="master-editor-locate-requirement-input"
                    onChange={(event) => updateDraft("locate_required", event.target.value === "required")}
                    value={draft.locate_required ? "required" : "not_required"}
                  >
                    <option value="not_required">Not Required</option>
                    <option value="required">Required</option>
                  </select>
                </Field>}
                {canManageRecords && <Field label="Water Locate Requirement" testId="master-editor-water-locate-state">
                  <select onChange={(event) => {
                    const required = event.target.value === "required";
                    updateDraft("water_locate_required", required);
                  }} disabled={requiresWaterLocate(draft.city)} value={draft.water_locate_required ? "required" : "not_required"}>
                    <option value="not_required">Not Required</option>
                    <option value="required">Required</option>
                  </select>
                </Field>}
                {canManageRecords && draft.water_locate_required && <Field label="Water locate submitted" testId="master-editor-water-locate-submitted">
                  <TypedDateInput data-testid="master-quick-water-locate-submitted" onChange={(value) => updateDraft("water_locate_submitted_at", value)} value={draft.water_locate_submitted_at} />
                </Field>}
                <Field label="Work scope" testId="master-editor-work-scope" wide>
                  <textarea onChange={(event) => updateDraft("work_scope", event.target.value)} value={draft.work_scope} />
                </Field>
                <Field label="What is left of work" testId="master-editor-remaining-work" wide>
                  <textarea onChange={(event) => updateDraft("remaining_work", event.target.value)} value={draft.remaining_work} />
                </Field>
                {canAccessBilling && <Field label="What is invoiced" testId="master-editor-invoiced-work" wide>
                  <textarea onChange={(event) => updateDraft("what_is_invoiced", event.target.value)} value={draft.what_is_invoiced} />
                </Field>}
                <Field label="Project notes" testId="master-editor-notes" wide>
                  <div className="qe-general-notes" data-testid="master-editor-general-notes">
                    <textarea
                      className="qe-note-input"
                      data-testid="master-editor-add-note-input"
                      onChange={(event) => setNoteDraft(event.target.value)}
                      placeholder="Add a project note. It becomes one entry in this project's Communication History."
                      value={noteDraft}
                    />
                    <button
                      className="qe-add-note-btn"
                      data-testid="master-editor-add-note-button"
                      disabled={addingNote || !noteDraft.trim()}
                      onClick={addGeneralNote}
                      type="button"
                    >
                      <Plus size={14} /> {addingNote ? "Adding…" : "Add note"}
                    </button>

                    <div className="qe-notes-list">
                      {generalNotes.length === 0 && (
                        <p className="qe-notes-empty" data-testid="master-editor-notes-empty">
                          No project notes yet.
                        </p>
                      )}
                      {generalNotes.slice(0, 5).map((note) => (
                        <div className="qe-note-item" data-testid={`master-editor-note-${note.id}`} key={note.id}>
                          <p className="qe-note-body">{note.original_note || note.notes}</p>
                          <span className="qe-note-meta">
                            {note.created_by_name_snapshot || "—"} · {formatTimestamp(note.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                    {generalNotes.length > 5 && (
                      <Link
                        className="qe-notes-history-link"
                        data-testid="master-editor-notes-history"
                        to={`/projects/${projectId}`}
                      >
                        View full history ({generalNotes.length})
                      </Link>
                    )}
                  </div>
                </Field>
              </div>
              <DialogFooter>
                <button data-testid="master-editor-project-save" disabled={savingProject} type="submit">
                  <Save size={15} />
                  {savingProject ? "Saving…" : "Save project information"}
                </button>
              </DialogFooter>
            </form>
          )}

          {activePanel === "stage" && <section data-testid="master-editor-stage-panel">
            <div className="master-quick-editor-grid">
              <Field label="Project Stage" testId="master-editor-stage-select">
                <select onChange={(event) => setStage(event.target.value)} value={stage}>
                  {stageChoices.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              {["complete", "closed"].includes(stage) && <Field label={stage === "closed" ? "Closure notes" : "Completion notes"} testId="master-editor-completion-notes" wide>
                <textarea data-testid="master-editor-completion-notes-input" onChange={(event) => setCompletionNotes(event.target.value)} placeholder={stage === "closed" ? "Optional closure notes" : "Optional completion notes"} value={completionNotes} />
              </Field>}
              {canManageRecords && <Field label="Roadblocked" testId="master-editor-blocked-toggle">
                <label className="master-editor-check-label">
                  <input checked={blockerActive} data-testid="master-editor-blocked-toggle-input" onChange={(event) => setBlockerActive(event.target.checked)} type="checkbox" />
                  Mark this project as Roadblocked
                </label>
              </Field>}
              {canManageRecords && (blockerActive || stage === "blocked") && <Field label="Blocker reason" testId="master-editor-blocker-reason" wide>
                <textarea onChange={(event) => setBlockerReason(event.target.value)} value={blockerReason} />
              </Field>}
            </div>
            <DialogFooter>
              <button data-testid="master-editor-stage-save" disabled={savingStage} onClick={saveStage} type="button">
                <Save size={15} />
                {savingStage ? "Saving…" : "Save official status"}
              </button>
            </DialogFooter>
          </section>}

          {activePanel === "permits" && <SupportRecordEditor canManageRecords={canManageRecords} creating={creatingType === "permits"} onAdd={() => createSupportRecord("permits")} onChange={updateSupportField} onDelete={deleteSupportRecord} onSave={saveSupportRecord} readiness={project?.readiness} records={permits} savingRecordId={savingRecordId} title="Active permit records" />}
          {activePanel === "locates" && <SupportRecordEditor canManageRecords={canManageRecords} creating={creatingType === "locates"} onAdd={() => createSupportRecord("locates")} onChange={updateSupportField} onDelete={deleteSupportRecord} onSave={saveSupportRecord} readiness={project?.readiness} records={locates} savingRecordId={savingRecordId} title="Active locate / 811 records" />}

          {activePanel === "platform" && <section className="master-editor-readonly-panel" data-testid="master-editor-platform-panel">
            <strong>Added to platform</strong>
            <p>{project.created_at ? formatTimestamp(project.created_at) : "This project does not have a platform-added timestamp."}</p>
            <p>This audit timestamp is protected and cannot be changed.</p>
          </section>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ children, label, testId, wide = false }) {
  const fieldControl = isValidElement(children)
    ? cloneElement(children, { "data-testid": `${testId}-input` })
    : children;
  return <div className={wide ? "master-editor-field is-wide" : "master-editor-field"} data-testid={testId}>
    <span>{label}</span>
    {fieldControl}
  </div>;
}

function SupportRecordEditor({ canManageRecords, creating, onAdd, onChange, onDelete, onSave, readiness, records, savingRecordId, title }) {
  return <section className="master-support-editor" data-testid={`master-editor-${title.includes("locate") ? "locates" : "permits"}-panel`}>
    <div className="master-support-editor-heading">
      <div>
        <span className="operations-kicker">Board source records</span>
        <h3>{title}</h3>
      </div>
      {canManageRecords && <button data-testid={`master-editor-add-${title.includes("locate") ? "locate" : "permit"}`} disabled={creating} onClick={onAdd} type="button"><Plus size={15} />{creating ? "Adding…" : "Add"}</button>}
    </div>
    {records.length === 0 ? <p className="master-editor-empty-records">No active records. Add one here without leaving the board.</p> : records.map((record) => {
      const isPermit = record.permit_type === "Permit";
      return <section className="master-support-record" data-testid={`master-editor-support-record-${record.id}`} key={record.id}>
      <div className="master-quick-editor-grid">
        <Field label="Reference / ticket number" testId={`master-editor-reference-${record.id}`}>
          <input disabled={!canManageRecords} onChange={(event) => onChange(record.id, "reference_number", event.target.value)} value={record.reference_number || ""} />
        </Field>
        {isPermit ? (
          <Field label="Received on" testId={`master-editor-received-${record.id}`}>
            <div className="master-editor-received-row">
              <TypedDateInput data-testid={`master-permit-received-${record.id}`} disabled={!canManageRecords} onChange={(value) => onChange(record.id, "received_at", value)} value={record.received_at || ""} />
              {canManageRecords && <button className="board-refresh-button" data-testid={`master-permit-mark-received-${record.id}`} onClick={() => onChange(record.id, "received_at", todayISO())} type="button">Mark received today</button>}
              <PermitCalculatedBadge readiness={readiness} testId={`master-permit-calculated-${record.id}`} />
            </div>
            <div className="master-editor-check-label">
              <input checked={Boolean(record.does_not_expire)} data-testid={`master-permit-does-not-expire-${record.id}`} disabled={!canManageRecords} onChange={(event) => { const c = event.target.checked; onChange(record.id, "does_not_expire", c); if (c) onChange(record.id, "expires_on", ""); }} type="checkbox" /><span>This permit does not expire</span>
            </div>
          </Field>
        ) : (
          <Field label="Locate condition" testId={`master-editor-status-${record.id}`}>
            <select disabled={!canManageRecords} onChange={(event) => onChange(record.id, "status", event.target.value)} value={record.status || "valid"}>
              {!LOCATE_CONDITIONS.includes(record.status) && <option value={record.status}>{formatInformationalStatus(record.status)}</option>}
              <option value="valid">Valid</option>
              <option value="trouble">Trouble</option>
              <option value="remark">Remark</option>
            </select>
          </Field>
        )}
        <Field label="Request date" testId={`master-editor-request-date-${record.id}`}>
          <TypedDateInput data-testid={`master-permit-request-${record.id}`} disabled={!canManageRecords} onChange={(value) => onChange(record.id, "request_date", value)} value={record.request_date || ""} />
        </Field>
        <Field label={isPermit ? "Expiration date" : "Expiration date (if known)"} testId={`master-editor-expires-date-${record.id}`}>
          <TypedDateInput data-testid={`master-permit-expires-${record.id}`} disabled={!canManageRecords || (isPermit && Boolean(record.does_not_expire))} onChange={(value) => onChange(record.id, "expires_on", value)} value={isPermit && record.does_not_expire ? "" : (record.expires_on || "")} />
        </Field>
        {!isPermit && <Field label="Locate / 811 ticket not needed" testId={`master-editor-ticket-not-needed-${record.id}`}>
          <div className="master-editor-check-label"><input checked={Boolean(record.ticket_not_needed)} disabled={!canManageRecords} onChange={(event) => onChange(record.id, "ticket_not_needed", event.target.checked)} type="checkbox" /><span>Ticket not needed</span></div>
        </Field>}
        <Field label="Notes" testId={`master-editor-support-notes-${record.id}`} wide>
          <textarea disabled={!canManageRecords} onChange={(event) => onChange(record.id, "notes", event.target.value)} value={record.notes || ""} />
        </Field>
        {!isPermit && <Field label="Ticket description" testId={`master-editor-ticket-description-${record.id}`} wide>
          <textarea disabled={!canManageRecords} onChange={(event) => onChange(record.id, "ticket_description", event.target.value)} value={record.ticket_description || ""} />
        </Field>}
      </div>
      {canManageRecords && <div className="master-support-record-actions"><button data-testid={`master-editor-save-support-${record.id}`} disabled={savingRecordId === record.id} onClick={() => onSave(record)} type="button"><Save size={15} />{savingRecordId === record.id ? "Saving…" : `Save ${record.permit_type}`}</button><button data-testid={`master-editor-delete-support-${record.id}`} disabled={savingRecordId === record.id} onClick={() => onDelete(record)} type="button">Delete</button></div>}
    </section>;
    })}
  </section>;
}

function supportPayload(record) {
  const base = {
    permit_type: record.permit_type,
    reference_number: record.reference_number || "",
    request_date: record.request_date || "",
    expires_on: record.expires_on || "",
    notes: record.notes || "",
    ticket_not_needed: Boolean(record.ticket_not_needed),
    ticket_description: record.ticket_description || "",
    active: record.active !== false,
  };
  if (record.permit_type === "Permit") {
    // Permit: humans record only the received fact; JFM calculates readiness.
    base.received_at = record.received_at || "";
    base.does_not_expire = Boolean(record.does_not_expire);
    if (base.does_not_expire) base.expires_on = "";
  } else {
    base.status = record.status || "valid";
  }
  return base;
}
